import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Auth
import { useAuth, AuthProvider } from './hooks/useAuth';
import { useSubscription } from './hooks/useSubscription';
import { trialService } from './services/trialService';

// Components (chargés immédiatement car petits et critiques)
import Layout from './components/layout/Layout';
import MessengerNotificationPopup from './components/notifications/MessengerNotificationPopup';
import LoadingSpinner from './components/common/LoadingSpinner';
import SubscriptionBanner from './components/common/SubscriptionBanner';
import VersionDisplay from './components/common/VersionDisplay';

// Hooks
import { useMessengerNotifications } from './hooks/useMessengerNotifications';

// Pages publiques (chargées immédiatement car souvent premières pages visitées)
import LandingPage from './pages/landing/LandingPage';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// Pages protégées (lazy loaded pour réduire le bundle initial)
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const RestaurantProfile = lazy(() => import('./pages/dashboard/RestaurantProfile'));
const MenuManagement = lazy(() => import('./pages/dashboard/MenuManagement'));
const Reviews = lazy(() => import('./pages/dashboard/Reviews'));
const Settings = lazy(() => import('./pages/settings/Settings'));
const Subscription = lazy(() => import('./pages/subscription/Subscription'));
const SubscriptionPurchase = lazy(() => import('./pages/subscription/SubscriptionPurchase'));
const SubscriptionSuccess = lazy(() => import('./pages/subscription/SubscriptionSuccess'));
const SubscriptionRenewal = lazy(() => import('./pages/subscription/SubscriptionRenewal'));
const PublicMenu = lazy(() => import('./pages/PublicMenu'));
const ReservationManagement = lazy(() => import('./pages/reservations/ReservationManagement'));
const SocialMedia = lazy(() => import('./pages/social/SocialMedia'));
const Messaging = lazy(() => import('./pages/messaging/Messaging'));
const WebsiteSection = lazy(() => import('./pages/website/WebsiteSection'));
const Welcome = lazy(() => import('./pages/welcome/Welcome'));

import './styles/globals.css';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const [hasTrialWithCGU, setHasTrialWithCGU] = React.useState<boolean | null>(null);
  const [hasEverHadTrial, setHasEverHadTrial] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    const checkTrialStatus = async () => {
      if (user) {
        const hasActiveTrial = await trialService.hasActiveTrialWithCGU(user.uid);
        const hasHadTrialBefore = await trialService.hasUserAlreadyHadTrial(user.uid);
        setHasTrialWithCGU(hasActiveTrial);
        setHasEverHadTrial(hasHadTrialBefore);
      }
    };

    if (user && !loading) {
      checkTrialStatus();
    }
  }, [user, loading]);

  if (loading || subscriptionLoading || hasTrialWithCGU === null || hasEverHadTrial === null) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si l'utilisateur a un abonnement Stripe actif, il ne doit pas voir /welcome
  if (subscription) {
    return <>{children}</>;
  }

  // Rediriger vers /welcome seulement si l'utilisateur n'a jamais eu de trial ET n'a pas d'abonnement
  if (!hasTrialWithCGU && !hasEverHadTrial) {
    return <Navigate to="/welcome" replace />;
  }

  // Si l'utilisateur a déjà eu un trial mais n'a plus de trial actif, rediriger vers subscription
  if (!hasTrialWithCGU && hasEverHadTrial) {
    return <Navigate to="/subscription" replace />;
  }

  return <>{children}</>;
};

// Surveillance des abonnements supprimée - vérification uniquement à 4h du matin via Cloud Function

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (user) {
    return <Navigate to="/redirect" replace />;
  }

  return <>{children}</>;
};

const SmartRedirectRoute: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const [hasTrialWithCGU, setHasTrialWithCGU] = React.useState<boolean | null>(null);
  const [hasEverHadTrial, setHasEverHadTrial] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    const checkTrialStatus = async () => {
      if (user) {
        const hasActiveTrial = await trialService.hasActiveTrialWithCGU(user.uid);
        const hasHadTrialBefore = await trialService.hasUserAlreadyHadTrial(user.uid);
        setHasTrialWithCGU(hasActiveTrial);
        setHasEverHadTrial(hasHadTrialBefore);
      }
    };

    if (user && !authLoading) {
      checkTrialStatus();
    }
  }, [user, authLoading]);

  if (authLoading || subscriptionLoading || hasTrialWithCGU === null || hasEverHadTrial === null) {
    return <LoadingSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Si l'utilisateur n'a jamais eu de trial, aller à Welcome
  if (!hasTrialWithCGU && !hasEverHadTrial) {
    return <Navigate to="/welcome" replace />;
  }

  // Si l'utilisateur a déjà eu un trial mais n'a plus de trial actif, aller à subscription
  if (!hasTrialWithCGU && hasEverHadTrial) {
    return <Navigate to="/subscription" replace />;
  }

  // Si l'utilisateur a un trial actif mais pas d'abonnement, aller au dashboard
  if (!subscription) {
    return <Navigate to="/dashboard" replace />;
  }

  // Sinon, aller au dashboard
  return <Navigate to="/dashboard" replace />;
};

function AppContent() {
  const { notification, clearNotification } = useMessengerNotifications();
  
  return (
    <div className="App">
      <MessengerNotificationPopup 
        notification={notification} 
        onClose={clearNotification} 
      />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
            {/* Routes publiques */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />

            
            {/* Public Routes */}
            <Route path="/menu/:restaurantId" element={<PublicMenu />} />

            
            {/* Landing Page */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Smart Redirect for authenticated users */}
            <Route path="/redirect" element={<SmartRedirectRoute />} />
            
            {/* Welcome Route */}
            <Route path="/welcome" element={
              <ProtectedRoute>
                <Welcome />
              </ProtectedRoute>
            } />
            
            {/* Protected Routes */}
            
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout pageTitle="Dashboard">
                  <SubscriptionBanner />
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/restaurant" element={
              <ProtectedRoute>
                <Layout pageTitle="Fiche Établissement">
                  <RestaurantProfile />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/menu" element={
              <ProtectedRoute>
                <Layout pageTitle="Menu & QR Code">
                  <MenuManagement />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/reviews" element={
              <ProtectedRoute>
                <Layout pageTitle="Avis Clients">
                  <Reviews />
                </Layout>
              </ProtectedRoute>
            } />
            

            
            <Route path="/reservations" element={
              <ProtectedRoute>
                <Layout pageTitle="Réservations">
                  <ReservationManagement />
                </Layout>
              </ProtectedRoute>
            } />
              
              <Route path="/social" element={
                <ProtectedRoute>
                  <Layout pageTitle="Réseaux Sociaux">
                    <SocialMedia />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/messaging" element={
                <ProtectedRoute>
                  <Layout pageTitle="Messagerie">
                    <Messaging />
                  </Layout>
                </ProtectedRoute>
              } />
              

              
              <Route path="/website" element={
              <ProtectedRoute>
                <Layout pageTitle="Site Web">
                  <WebsiteSection />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/subscription" element={
              <ProtectedRoute>
                <Layout pageTitle="Mon Abonnement">
                  <Subscription />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/subscription/purchase" element={
              <ProtectedRoute>
                <SubscriptionPurchase />
              </ProtectedRoute>
            } />
            
            <Route path="/subscription/success" element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            } />
            
            <Route path="/subscription/renewal" element={
              <ProtectedRoute>
                <SubscriptionRenewal />
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <Layout pageTitle="Paramètres">
                  <Settings />
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/orders" element={
              <ProtectedRoute>
                <Layout pageTitle="Commandes">
                  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                      {/* Header unifié */}
                      <div className="text-center mb-12">
                        <h1 className="text-4xl font-extralight text-gray-900 mb-3 tracking-tight">
                          Commandes
                        </h1>
                        <p className="text-lg text-gray-500 font-light">
                          Gérez et suivez toutes vos commandes
                        </p>
                      </div>
                      
                      <div className="space-y-8">
                        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl hover:scale-[1.01] transition-all duration-300">
                          <div className="p-8">
                            <div className="text-center py-16">
                              <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                </svg>
                              </div>
                              <h3 className="text-xl font-light text-gray-900 mb-3">
                                Fonctionnalité en développement
                              </h3>
                              <p className="text-gray-500 font-light">
                                La gestion des commandes sera bientôt disponible.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Layout>
              </ProtectedRoute>
            } />
            
            <Route path="/analytics" element={
              <ProtectedRoute>
                <Layout pageTitle="Analytics">
                  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
                    <div className="max-w-6xl mx-auto px-6 py-12">
                      {/* Header épuré style Apple */}
                      <div className="text-center mb-12">
                        <h1 className="text-4xl font-extralight text-gray-900 mb-3 tracking-tight">Analytics</h1>
                        <p className="text-lg text-gray-500 font-light">Analysez les performances de votre restaurant</p>
                      </div>
                      
                      {/* Contenu Analytics */}
                      <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl p-8 hover:shadow-2xl transition-all duration-300">
                        <div className="text-center py-12">
                          <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-full flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                          </div>
                          <h2 className="text-2xl font-extralight text-gray-900 mb-4">Analytics Avancées</h2>
                          <p className="text-gray-500 font-light">Cette fonctionnalité sera bientôt disponible.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Layout>
              </ProtectedRoute>
            } />
            

            

        </Routes>
      </Suspense>
            
      <ToastContainer
              position="top-right"
              autoClose={3000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="light"
            />
            <VersionDisplay />
          </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
